// users.js
'use strict';
import { fromJS, Set as ImmutableSet } from 'immutable';
import {
  ADD_USERS,
  MERGE_USER,
  LOGOUT,
  SET_USER,
  MERGE_SSR_DATA,
  // -- PLOP_PREPEND_REDUCER_ACTION_TYPE --
} from '../ActionTypes.js';

export const defaultState = fromJS({});

/**
 * Create nested object by keys.
 *
 * Input: { keys: [ 'a', 'b' ], value: 123 }
 * Output:
 * {
 *   a: {
 *     b: 123
 *   }
 * }
 *
 * @param {array} keys - nested keys
 * @param {any} value - key's value
 * @return Return nested object.
 */
const createObjectByKeys = (keys = [], value) => {
  const object = {};
  return keys.reduce((accumulator, key, index) => {
    if (index === keys.length - 1) {
      accumulator[key] = value;
      return object;
    }
    accumulator[key] = {};
    return accumulator[key];
  }, object);
};

/**
 * Merge previous and next immutable map.
 * @param {Immutable.Map} previous - previous user immutable map.
 * @param {Immutable.Map} next - next user immutable map.
 * @param {array} keys - keys which needs list concatenation.
 * @return Return merged immutable map
 */
const mergeDeepWithListConcatenation = (
  previous = fromJS({}),
  next = fromJS({}),
  keys = []
) => {
  if (!keys.length) return previous.mergeDeep(next);
  let result = {};
  keys.forEach(key => {
    const selectPath = key.split('.');
    const prevList = previous.getIn(selectPath);
    const nextList = next.getIn(selectPath);
    if (!prevList && !nextList) return;
    const items = new ImmutableSet(
      (prevList || fromJS([])).concat(nextList || fromJS([]))
    );
    result = {
      ...result,
      ...createObjectByKeys(selectPath, items),
    };
  });
  return previous.mergeDeep(next, result);
};

/**
 * Users.
 * @module reducer/users
 */
const users = (state = defaultState, action) => {
  switch (action.type) {
    case ADD_USERS:
      return _ADD_USERS(action.payload)(state);
    case MERGE_USER:
      return _MERGE_USER(action.payload)(state);
    case LOGOUT:
      return _LOGOUT(action.payload)(state);
    case SET_USER:
      return _SET_USER(action.payload)(state);
    case MERGE_SSR_DATA:
      return _MERGE_SSR_DATA(action.payload)(state);
    // -- PLOP_PREPEND_REDUCER_SWITCH_CASE --
    default:
      return state;
  }
};

/**
 * Add users
 * @kind reducer/actionType
 * @name ADD_USERS
 * @param {object} {users} - users using user id as keys
 * @return {Immutable.Map} New state
 */
const _ADD_USERS =
  ({ users: paramUsers }) =>
  state => {
    if (!paramUsers) return state;
    return state.mergeDeep(
      fromJS(paramUsers).map((user, userId) =>
        mergeDeepWithListConcatenation(state.get(userId), user, ['badges'])
      )
    );
  };

/**
 * Merge user
 * @kind reducer/actionType
 * @name MERGE_USER
 * @param {object} {user} - user object.
 * @param {string} {user.id} - user id.
 * @return {Immutable.Map} New state
 */
const _MERGE_USER =
  ({ user }) =>
  state => {
    if (!user) return state;
    const prevUser = state.get(user.id)?.toJS() || Object.create(null);
    if (user.displayNameLastUpdated) {
      // As of Oct. 2023, there are only two origins which may contain display name:
      // 1. User detail API
      // 2. CHAT_MESSAGE_SENT event
      // And we've calculated corresponding `displayNameLastUpdated` in fetchUser.js and handleStreamChatSent.js
      // by making use of response's header and event's timestamp respectively.
      if (
        !prevUser.displayNameLastUpdated ||
        user.displayNameLastUpdated > prevUser.displayNameLastUpdated
      ) {
        return state.mergeDeepIn(
          [user.id],
          mergeDeepWithListConcatenation(state.get(user.id), fromJS(user), [
            'badges',
          ])
        );
      }

      // Still merge all the fields except for the "displayName"
      const { displayName, ...toBeMerged } = user;

      return state.mergeDeepIn(
        [user.id],
        mergeDeepWithListConcatenation(state.get(user.id), fromJS(toBeMerged), [
          'badges',
        ])
      );
    }

    return state.mergeDeepIn(
      [user.id],
      mergeDeepWithListConcatenation(state.get(user.id), fromJS(user), [
        'badges',
      ])
    );
  };

/**
 * Logout
 * @kind reducer/actionType
 * @name LOGOUT
 * @return {Immutable.Map} New state
 */
const _LOGOUT = () => () => {
  return defaultState;
};

/**
 * Set user
 * @kind reducer/actionType
 * @name SET_USER
 * @param {array} selectPath - select path
 * @param {object} data - data
 * @return {Immutable.Map} New state
 */
const _SET_USER =
  ({ selectPath, data }) =>
  state => {
    return state.setIn([...selectPath], fromJS(data));
  };

/**
 * Merge SSR data
 * @description
 * This action is used to merge SSR data into users reducer store.
 * Usually, it's used in route loader.
 * @kind reducer/actionType
 * @name MERGE_SSR_DATA
 * @param {object} {users} - users reducer store from SSR
 * @return {Immutable.Map} New state
 */
const _MERGE_SSR_DATA =
  ({ users: paramUsers }) =>
  state => {
    return state.mergeDeep(fromJS(paramUsers));
  };

// -- PLOP_PREPEND_REDUCER_ACTION_HANDLER --

export default users;
